import { Tooltip, TooltipProps } from '@sgme/ui';
import type { ICellRendererParams } from 'ag-grid-community';
import { useAppSelector } from 'hooks/useAppSelector';
import { FC, ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { AppState } from 'state/state.model';
import { CustomIconSpinner } from 'utils/customSpinner';

import type { EditableFieldInfo } from '../blotter.model';
export type EditableContentSelector<RowId, ColumnId, EditableField extends EditableFieldInfo> = (
  s: AppState,
  rowId?: RowId,
  colId?: ColumnId,
) => EditableField | undefined;
export const editableContentCellRendererFactory =
  <RowId, EditableField extends EditableFieldInfo, Data extends { rowId: RowId }, ColumnId = keyof Data>(
    editableContentSelector: EditableContentSelector<RowId, ColumnId, EditableField>,
    renderer: FC<ICellRendererParams> | undefined = ({ value }) => value,
  ) =>
  (renderParams: ICellRendererParams<Data>) => {
    const { data, colDef } = renderParams;
    const editInfo = useAppSelector(s => editableContentSelector(s, data?.rowId, colDef?.field as ColumnId));
    const { formatMessage } = useIntl();
    const defaultMessage = useMemo(() => {
      if (editInfo?.status === 'failed' && editInfo?.status !== undefined && !editInfo?.errors && !editInfo?.warnings) {
        return formatMessage({ id: 'generic.error.message' });
      } else {
        return [editInfo?.warnings, editInfo?.errors].join('\n').trim();
      }
    }, [editInfo, formatMessage]);
    const tooltipColor: TooltipProps['color'] =
      editInfo?.warnings && editInfo?.status !== 'failed' ? 'warning' : 'danger';

    const cellContent = (
      <span className="align-items-center" data-e2e={colDef?.editable ? 'editable-message' : 'not-editable-message'}>
        {editInfo?.status === 'loading' && <CustomIconSpinner size="sm" className="me-1" color="text-info" />}
        {editInfo?.status === 'done' && <i className="me-1 icon text-success">done</i>}
        {editInfo?.status === 'failed' && <i className="me-1 icon text-danger">close</i>}
        {editInfo?.warnings && <i className="me-1 icon text-warning">warning</i>}
        {renderer(renderParams) as ReactNode}
      </span>
    );

    return defaultMessage ? (
      <Tooltip position="right" contents={defaultMessage} color={tooltipColor}>
        {cellContent}
      </Tooltip>
    ) : (
      cellContent
    );
  };
